<template>
  <section class="panel">
    <p class="label">{{ t('guess-box.label') }}</p>
    <div class="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
      <input
        v-model="guess"
        class="input"
        :placeholder="t('guess-box.placeholder')"
        data-testid="guess-input"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        @keydown.enter.prevent="handleGuess"
      />
      <button
        class="btn-primary"
        type="button"
        @click="handleGuess"
        data-testid="guess-check"
      >
        {{ t('guess-box.button-check') }}
      </button>
      <button
        class="btn-secondary"
        type="button"
        @click="handleHint"
        data-testid="hint-button"
      >
        {{ t('guess-box.button-hint') }}
      </button>
      <button
        class="btn-secondary whitespace-nowrap"
        type="button"
        @click="handleGiveUp"
        data-testid="give-up"
      >
        {{ t('guess-box.button-skip') }}
      </button>
      <button
        class="btn-secondary whitespace-nowrap uppercase"
        type="button"
        @click="handleChangeParameters"
        data-testid="change-parameters"
      >
        {{ t('common.change-parameters') }}
      </button>
    </div>
    <p
      v-if="feedback"
      class="mt-3 text-sm font-semibold text-rose-600"
      data-testid="guess-feedback"
    >
      {{ feedback }}
    </p>
  </section>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { isFuzzyMatch } from "../utils/fuzzyMatch";
import { useText } from "../composables/useText.js";

const router = useRouter();
const { t } = useText();

const props = defineProps({
  playerId: {
    type: [String, Number],
    default: "",
  },
  playerName: {
    type: String,
    default: "",
  },
});

const guess = ref("");
const feedback = ref("");
const hintLength = ref(0);

const getGuessName = (name) => {
  const trimmed = name?.trim() || "";
  if (!trimmed) return "";
  return trimmed;
};

const hintTarget = computed(() => getGuessName(props.playerName || ""));

const countHintLetters = (value) => {
  if (!value) return 0;
  return value.replace(/\s/g, "").length;
};

const splitHintTarget = (value) => {
  const match = value.match(/^((?:\p{L}\.\s+)+)(.*)$/u);
  if (!match) {
    return { prefix: "", remainder: value };
  }
  return { prefix: match[1] || "", remainder: match[2] || "" };
};

const buildHintValue = (value, lettersToReveal) => {
  if (!value || lettersToReveal <= 0) return "";
  let lettersSeen = 0;
  let endIndex = -1;

  for (let index = 0; index < value.length; index += 1) {
    if (!/\s/.test(value[index])) {
      lettersSeen += 1;
      if (lettersSeen >= lettersToReveal) {
        endIndex = index;
        break;
      }
    }
  }

  if (endIndex === -1) {
    endIndex = value.length - 1;
  }

  return value.slice(0, endIndex + 1);
};

const handleGuess = () => {
  feedback.value = "";

  if (!guess.value.trim()) {
    feedback.value = t('guess-box.error-empty');
    return;
  }

  if (isFuzzyMatch(guess.value, props.playerName || "")) {
    router.push({ name: "win" });
  } else {
    router.push({ name: "lose" });
  }
};

const handleGiveUp = () => {
  router.push({ name: "lose" });
};

const handleChangeParameters = () => {
  router.push({ name: "form" });
};

const handleHint = () => {
  const currentTarget = hintTarget.value;
  if (!currentTarget) return;

  const { prefix, remainder } = splitHintTarget(currentTarget);
  if (!remainder) return;

  const totalLetters = countHintLetters(remainder);
  const nextLength = Math.min(hintLength.value + 1, totalLetters);
  hintLength.value = nextLength;
  feedback.value = "";
  guess.value = `${prefix}${buildHintValue(remainder, nextLength)}`;

  if (nextLength >= totalLetters) {
    router.push({ name: "lose" });
  }
};

watch(
  () => props.playerId,
  () => {
    guess.value = "";
    feedback.value = "";
    hintLength.value = 0;
  },
);
</script>
